export interface EncryptedPayload {
  ciphertext: string;
  iv: string;
  salt: string;
}

// Brand type to enforce safe ArrayBuffer (not SharedArrayBuffer)
type SafeUint8Array = Uint8Array & { buffer: ArrayBuffer };

export class CryptoWrapper {
  private static instance: CryptoWrapper;

  static getInstance(): CryptoWrapper {
    if (!CryptoWrapper.instance) {
      CryptoWrapper.instance = new CryptoWrapper();
    }
    return CryptoWrapper.instance;
  }

  private readonly encoder = new TextEncoder();
  private readonly decoder = new TextDecoder();

  public encode = (str: string): Uint8Array => this.encoder.encode(str);
  public decode = (buf: ArrayBuffer): string => this.decoder.decode(buf);

  private readonly toBase64 = (bytes: Uint8Array): string =>
    btoa(String.fromCharCode(...bytes));

  private readonly fromBase64 = (b64: string): Uint8Array =>
    Uint8Array.from(atob(b64), c => c.charCodeAt(0));

  private constructor() {}

  // Ensures buffer is backed by plain ArrayBuffer (not SharedArrayBuffer)
  private toSafeUint8Array(input: Uint8Array): SafeUint8Array {
    const slice = input.buffer.slice(input.byteOffset, input.byteOffset + input.byteLength);
    return new Uint8Array(slice) as SafeUint8Array;
  }

  async deriveKey(password: string, salt: SafeUint8Array): Promise<CryptoKey> {
    const encodedPassword = this.toSafeUint8Array(this.encode(password));
    const keyMaterial = await crypto.subtle.importKey(
      "raw",
      encodedPassword,
      { name: "PBKDF2" },
      false,
      ["deriveKey"]
    );

    return crypto.subtle.deriveKey(
      {
        name: "PBKDF2",
        salt,
        iterations: 100000,
        hash: "SHA-256",
      },
      keyMaterial,
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"]
    );
  }

  async encryptObject(
    obj: any,
    password: string,
    saltString: string
  ): Promise<EncryptedPayload> {
    const ivRaw = crypto.getRandomValues(new Uint8Array(12));
    const iv = this.toSafeUint8Array(ivRaw);

    const saltRaw = this.encode(saltString);
    const salt = this.toSafeUint8Array(saltRaw);

    const key = await this.deriveKey(password, salt);

    const encoded = this.encode(JSON.stringify(obj));
    const safePlaintext = this.toSafeUint8Array(encoded);

    const ciphertextBuffer = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      key,
      safePlaintext
    );

    return {
      ciphertext: this.toBase64(new Uint8Array(ciphertextBuffer)),
      iv: this.toBase64(iv),
      salt: saltString,
    };
  }

  async decryptObject(
    ciphertextB64: string,
    ivB64: string,
    password: string,
    saltString: string
  ): Promise<any> {
    try {
      const ivRaw = this.fromBase64(ivB64);
      const iv = this.toSafeUint8Array(ivRaw);

      const ciphertextRaw = this.fromBase64(ciphertextB64);
      const ciphertext = this.toSafeUint8Array(ciphertextRaw);

      const saltRaw = this.encode(saltString);
      const salt = this.toSafeUint8Array(saltRaw);

      const key = await this.deriveKey(password, salt);

      const decrypted = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv },
        key,
        ciphertext
      );

      return JSON.parse(this.decode(decrypted));
    } catch {
      throw new Error("Decryption failed. Possible incorrect password or corrupt data.");
    }
  }
}
